import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

// Module-level cache: avoids redundant DB queries across components on the same session.
let _cache: { value: boolean; ts: number } | null = null;
const CACHE_TTL = 60_000;

export function invalidatePlayerRoleCache() {
  _cache = null;
}

/**
 * Returns true if the logged-in user has only "player" memberships
 * and owns no team. Used to restrict access to PlayCreator and EditPlay.
 */
export function useIsPlayerOnly() {
  const [isPlayerOnly, setIsPlayerOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (_cache && Date.now() - _cache.ts < CACHE_TTL) {
      setIsPlayerOnly(_cache.value);
      setLoading(false);
      return;
    }

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) { setLoading(false); return; }

      // Does user own any team?
      const { data: owned } = await supabase
        .from("teams")
        .select("id")
        .eq("user_id", user.id)
        .limit(1);

      if (owned && owned.length > 0) {
        _cache = { value: false, ts: Date.now() };
        if (!cancelled) { setIsPlayerOnly(false); setLoading(false); }
        return;
      }

      // Does user have any active non-player membership (coach, assistant, viewer)?
      const { data: staff } = await supabase
        .from("team_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .neq("role", "player")
        .limit(1);

      if (staff && staff.length > 0) {
        _cache = { value: false, ts: Date.now() };
        if (!cancelled) { setIsPlayerOnly(false); setLoading(false); }
        return;
      }

      // Does user have at least one active player membership?
      const { data: playerMem } = await supabase
        .from("team_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .eq("role", "player")
        .limit(1);

      const result = !!(playerMem && playerMem.length > 0);
      _cache = { value: result, ts: Date.now() };
      if (!cancelled) { setIsPlayerOnly(result); setLoading(false); }
    })();

    return () => { cancelled = true; };
  }, []);

  return { isPlayerOnly, loading };
}
